import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllInvoicesOptions,
	getAllPaymentsOptions,
	getAllParentsOptions,
	processAutomaticPaymentMutation,
	createManualPaymentMutation,
	createDebtForgivenessMutation,
	previewPaymentMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
	CreditCard,
	Euro,
	AlertTriangle,
	XCircle,
	DollarSign,
	Zap,
	Gift,
	Eye,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import React, { useState, useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterFaceted,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";

export const Route = createFileRoute("/admin/payments/")({
	component: PaymentsManagementPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			invoiceId: search.invoiceId as string | undefined,
			parentId: search.parentId as string | undefined,
		};
	},
});

const statusOptions = [
	{ label: "Réussi", value: "SUCCEEDED" },
	{ label: "Échec", value: "FAILED" },
	{ label: "En cours", value: "PENDING" },
];

const paymentMethodOptions = [
	{ label: "Carte", value: "CARD" },
	{ label: "SEPA", value: "SEPA" },
	{ label: "Virement", value: "BANK_TRANSFER" },
	{ label: "Manuel", value: "MANUAL" },
];

const paymentProviderOptions = [
	{ label: "Stripe", value: "STRIPE" },
	{ label: "HelloAsso", value: "HELLOASSO" },
	{ label: "Effacement", value: "INTERNAL_DEBT_FORGIVENESS" },
];

const columns: ColumnDef<any, any>[] = [
	{
		accessorKey: "createdAt",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Date" />
		),
		cell: ({ row }) => {
			const value = row.getValue("createdAt") as string;
			return (
				<span className="text-sm">
					{value
						? format(new Date(value), "dd/MM/yyyy HH:mm", { locale: fr })
						: "N/A"}
				</span>
			);
		},
		meta: {
			label: "Date",
			textValue: (row: any) =>
				row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }) : "N/A",
		},
	},
	{
		id: "parent",
		accessorFn: (row: any) =>
			(row.parent?.lastName || "") + " " + (row.parent?.firstName || ""),
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Parent" />
		),
		cell: ({ row }) => {
			const payment = row.original as any;
			return (
				<div>
					<p className="text-sm font-medium">
						{payment.parent?.firstName} {payment.parent?.lastName}
					</p>
					<p className="text-xs text-gray-500">{payment.parent?.email}</p>
				</div>
			);
		},
		filterFn: (row, columnId, filterValue: string) => {
			const payment = row.original as any;
			const searchValue = normalizeString(filterValue);
			const fullName = normalizeString(
				`${payment.parent?.firstName || ""} ${payment.parent?.lastName || ""}`
			);
			const reverseName = normalizeString(
				`${payment.parent?.lastName || ""} ${payment.parent?.firstName || ""}`
			);
			const email = normalizeString(payment.parent?.email || "");
			return (
				fullName.includes(searchValue) ||
				reverseName.includes(searchValue) ||
				email.includes(searchValue)
			);
		},
		sortDescFirst: false,
		meta: { label: "Parent" },
	},
	{
		accessorKey: "amount",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Montant" />
		),
		cell: ({ row }) => {
			const payment = row.original as any;
			return (
				<div>
					<p className="font-medium">{payment.amount?.toFixed(2)} €</p>
					{payment.refunded && (
						<p className="text-xs text-red-600">
							Remboursé: {payment.refundAmount?.toFixed(2)} €
						</p>
					)}
				</div>
			);
		},
		meta: {
			label: "Montant",
			textValue: (row: any) => `${row.amount?.toFixed(2)} €`,
		},
	},
	{
		accessorKey: "paymentMethod",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Méthode" />
		),
		cell: ({ row }) => {
			const value = row.getValue("paymentMethod") as string;
			return (
				<Badge variant="outline">
					{value === "CARD" && "Carte"}
					{value === "SEPA" && "SEPA"}
					{value === "BANK_TRANSFER" && "Virement"}
					{value === "MANUAL" && "Manuel"}
				</Badge>
			);
		},
		filterFn: (row, id, value: string[]) =>
			value.includes(String(row.getValue(id))),
		meta: {
			label: "Méthode",
			textValue: (row: any) => {
				const labels: Record<string, string> = { CARD: "Carte", SEPA: "SEPA", BANK_TRANSFER: "Virement", MANUAL: "Manuel" };
				return labels[row.paymentMethod] || row.paymentMethod || "";
			},
		},
	},
	{
		accessorKey: "paymentProvider",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Provider" />
		),
		cell: ({ row }) => {
			const value = row.getValue("paymentProvider") as string;
			return (
				<Badge variant="secondary">
					{value === "STRIPE" && "Stripe"}
					{value === "HELLOASSO" && "HelloAsso"}
					{value === "INTERNAL_DEBT_FORGIVENESS" && "Effacement"}
				</Badge>
			);
		},
		filterFn: (row, id, value: string[]) =>
			value.includes(String(row.getValue(id))),
		meta: {
			label: "Provider",
			textValue: (row: any) => {
				const labels: Record<string, string> = { STRIPE: "Stripe", HELLOASSO: "HelloAsso", INTERNAL_DEBT_FORGIVENESS: "Effacement" };
				return labels[row.paymentProvider] || row.paymentProvider || "";
			},
		},
	},
	{
		accessorKey: "status",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Statut" />
		),
		cell: ({ row }) => {
			const value = row.getValue("status") as string;
			return (
				<Badge
					variant={
						value === "SUCCEEDED"
							? "default"
							: value === "FAILED"
								? "destructive"
								: "secondary"
					}
				>
					{value === "SUCCEEDED" && "Réussi"}
					{value === "FAILED" && "Échec"}
					{value === "PENDING" && "En cours"}
				</Badge>
			);
		},
		filterFn: (row, id, value: string[]) =>
			value.includes(String(row.getValue(id))),
		meta: {
			label: "Statut",
			textValue: (row: any) => {
				const labels: Record<string, string> = { SUCCEEDED: "Réussi", FAILED: "Échec", PENDING: "En cours" };
				return labels[row.status] || row.status || "";
			},
		},
	},
	{
		id: "actions",
		header: () => <span>Actions</span>,
		cell: ({ row }) => {
			const payment = row.original as any;
			return (
				<Link to="/admin/payments/$id" params={{ id: payment.id }}>
					<Button size="sm" variant="ghost">
						<Eye className="h-4 w-4" />
					</Button>
				</Link>
			);
		},
		enableSorting: false,
		meta: { label: "Actions" },
	},
];

function PaymentsManagementPage() {
	const queryClient = useQueryClient();
	const search = Route.useSearch();

	// State for automatic payment dialog
	const [processAutomaticDialogOpen, setProcessAutomaticDialogOpen] = useState(false);

	// State for manual payment dialog
	const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);
	const [selectedParent, setSelectedParent] = useState<any>(null);
	const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
	const [useCustomAmount, setUseCustomAmount] = useState(false);
	const [customAmount, setCustomAmount] = useState("");
	const [paymentMethod, setPaymentMethod] = useState<"CARD" | "SEPA" | "BANK_TRANSFER" | "MANUAL">("MANUAL");
	const [paymentProvider, setPaymentProvider] = useState<"STRIPE" | "HELLOASSO" | "INTERNAL_DEBT_FORGIVENESS">("HELLOASSO");
	const [comment, setComment] = useState("");
	const [preview, setPreview] = useState<any>(null);
	const [parentSearch, setParentSearch] = useState("");
	const [invoiceSearch, setInvoiceSearch] = useState("");
	const [searchMode, setSearchMode] = useState<"parent" | "invoice">("parent");

	// State for debt forgiveness dialog
	const [debtDialogOpen, setDebtDialogOpen] = useState(false);
	const [debtParent, setDebtParent] = useState<any>(null);
	const [debtInvoices, setDebtInvoices] = useState<string[]>([]);
	const [debtCustomAmount, setDebtCustomAmount] = useState("");
	const [debtUseCustomAmount, setDebtUseCustomAmount] = useState(false);
	const [debtComment, setDebtComment] = useState("");
	const [debtPreview, setDebtPreview] = useState<any>(null);

	const { data: invoicesData, isLoading: invoicesLoading } = useQuery(
		getAllInvoicesOptions(),
	);

	const { data: paymentsData, isLoading: paymentsLoading } = useQuery(
		getAllPaymentsOptions(),
	);

	const { data: parentsData } = useQuery(getAllParentsOptions());
	const parents = parentsData || [];

	const allPayments = paymentsData || [];
	const invoices = invoicesData || [];

	const isLoading = invoicesLoading || paymentsLoading;

	const processAutomatic = useMutation({
		...processAutomaticPaymentMutation(),
		onSuccess: (data) => {
			toast.success(
				`Paiements automatiques traités: ${(data as any)?.successCount || 0} réussi(s), ${(data as any)?.failedCount || 0} échec(s)`,
			);
			queryClient.invalidateQueries({ queryKey: ["invoices"] });
			queryClient.invalidateQueries({ queryKey: ["payments"] });
			setProcessAutomaticDialogOpen(false);
		},
		onError: () => {
			toast.error("Erreur lors du traitement des paiements automatiques");
		},
	});

	const previewMutation = useMutation({
		...previewPaymentMutation(),
		onSuccess: (data) => {
			setPreview(data);
		},
		onError: () => {
			toast.error("Erreur lors de la prévisualisation");
		},
	});

	const debtPreviewMutation = useMutation({
		...previewPaymentMutation(),
		onSuccess: (data) => {
			setDebtPreview(data);
		},
		onError: () => {
			toast.error("Erreur lors de la prévisualisation");
		},
	});

	const createManualPayment = useMutation({
		...createManualPaymentMutation(),
		onSuccess: () => {
			toast.success("Paiement manuel créé avec succès");
			queryClient.invalidateQueries({ queryKey: ["invoices"] });
			queryClient.invalidateQueries({ queryKey: ["payments"] });
			resetManualPaymentDialog();
		},
		onError: () => {
			toast.error("Erreur lors de la création du paiement");
		},
	});

	const createDebtForgiveness = useMutation({
		...createDebtForgivenessMutation(),
		onSuccess: () => {
			toast.success("Dette effacée avec succès");
			queryClient.invalidateQueries({ queryKey: ["invoices"] });
			queryClient.invalidateQueries({ queryKey: ["payments"] });
			resetDebtDialog();
		},
		onError: () => {
			toast.error("Erreur lors de l'effacement de la dette");
		},
	});

	const stats = {
		totalPayments: allPayments.length,
		successfulPayments: allPayments.filter((p: any) => p.status === "SUCCEEDED")
			.length,
		failedPayments: allPayments.filter((p: any) => p.status === "FAILED")
			.length,
		pendingPayments: allPayments.filter((p: any) => p.status === "PENDING")
			.length,
		totalAmount: allPayments.reduce(
			(acc: number, p: any) => acc + (p.amount || 0),
			0,
		),
		refundedAmount: allPayments
			.filter((p: any) => p.refunded)
			.reduce((acc: number, p: any) => acc + (p.refundAmount || 0), 0),
		pendingInvoices:
			invoices?.filter(
				(inv) => inv.status === "PENDING" || inv.status === "PARTIALLY_PAID",
			).length || 0,
	};

	const sortedPayments = useMemo(
		() =>
			[...allPayments]
				.filter((p: any) => p.status !== "PENDING")
				.sort(
					(a: any, b: any) =>
						new Date(b.createdAt || "").getTime() -
						new Date(a.createdAt || "").getTime(),
				),
		[allPayments],
	);

	const parentOutstandingInvoices = selectedParent
		? invoices?.filter(
				(inv) =>
					inv.parent?.id === selectedParent.id &&
					(inv.status === "PENDING" || inv.status === "PARTIALLY_PAID"),
			) || []
		: [];

	const debtParentOutstandingInvoices = debtParent
		? invoices?.filter(
				(inv) =>
					inv.parent?.id === debtParent.id &&
					(inv.status === "PENDING" || inv.status === "PARTIALLY_PAID"),
			) || []
		: [];

	const handleProcessAutomatic = () => {
		processAutomatic.mutate({} as any);
	};

	const resetManualPaymentDialog = () => {
		setManualPaymentDialogOpen(false);
		setSelectedParent(null);
		setSelectedInvoices([]);
		setUseCustomAmount(false);
		setCustomAmount("");
		setPaymentMethod("MANUAL");
		setPaymentProvider("HELLOASSO");
		setComment("");
		setPreview(null);
	};

	const resetDebtDialog = () => {
		setDebtDialogOpen(false);
		setDebtParent(null);
		setDebtInvoices([]);
		setDebtCustomAmount("");
		setDebtUseCustomAmount(false);
		setDebtComment("");
		setDebtPreview(null);
	};

	const handlePreviewManualPayment = () => {
		if (!selectedParent) return;

		previewMutation.mutate({
			body: {
				parentId: selectedParent.id,
				invoiceIds: useCustomAmount ? undefined : selectedInvoices,
				customAmount: useCustomAmount && customAmount ? parseFloat(customAmount) : undefined,
			},
		});
	};

	const handlePreviewDebt = () => {
		if (!debtParent) return;

		debtPreviewMutation.mutate({
			body: {
				parentId: debtParent.id,
				invoiceIds: debtUseCustomAmount ? undefined : debtInvoices,
				customAmount: debtUseCustomAmount && debtCustomAmount ? parseFloat(debtCustomAmount) : undefined,
			},
		});
	};

	const handleCreateManualPayment = () => {
		if (!selectedParent || !preview) return;

		createManualPayment.mutate({
			body: {
				parentId: selectedParent.id,
				amount: preview.totalAmount,
				paymentMethod,
				paymentProvider,
				status: "SUCCEEDED",
				comment: comment || undefined,
				invoiceIds: preview.allocations.map((a: any) => a.invoiceId),
			},
		});
	};

	const handleCreateDebtForgiveness = () => {
		if (!debtParent || !debtPreview || !debtComment) return;

		createDebtForgiveness.mutate({
			body: {
				parentId: debtParent.id,
				amount: debtPreview.totalAmount,
				paymentMethod: "MANUAL",
				paymentProvider: "INTERNAL_DEBT_FORGIVENESS",
				status: "SUCCEEDED",
				comment: debtComment,
				invoiceIds: debtPreview.allocations.map((a: any) => a.invoiceId),
			},
		});
	};

	React.useEffect(() => {
		if (selectedParent && ((selectedInvoices.length > 0 && !useCustomAmount) || (useCustomAmount && customAmount))) {
			handlePreviewManualPayment();
		} else {
			setPreview(null);
		}
	}, [selectedParent, selectedInvoices, useCustomAmount, customAmount]);

	React.useEffect(() => {
		if (debtParent && ((debtInvoices.length > 0 && !debtUseCustomAmount) || (debtUseCustomAmount && debtCustomAmount))) {
			handlePreviewDebt();
		} else {
			setDebtPreview(null);
		}
	}, [debtParent, debtInvoices, debtUseCustomAmount, debtCustomAmount]);

	React.useEffect(() => {
		if (search.parentId && search.invoiceId && parents.length > 0 && invoices.length > 0) {
			const parent = parents.find((p: any) => p.id === search.parentId);
			if (parent) {
				setSelectedParent(parent);
				setParentSearch(`${parent.firstName} ${parent.lastName} - ${parent.email}`);
				setSelectedInvoices([search.invoiceId]);
				setManualPaymentDialogOpen(true);
			}
		}
	}, [search.parentId, search.invoiceId, parents, invoices]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement des paiements...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Gestion des paiements</h1>
					<p className="text-gray-600 mt-2">
						Traitez et suivez tous les paiements
					</p>
				</div>
				<div className="flex gap-2">
					<Button
						variant="outline"
						onClick={() => setManualPaymentDialogOpen(true)}
					>
						<DollarSign className="mr-2 h-4 w-4" />
						Paiement manuel
					</Button>
					<Button
						variant="outline"
						onClick={() => setDebtDialogOpen(true)}
					>
						<Gift className="mr-2 h-4 w-4" />
						Effacer une dette
					</Button>
					<Dialog
						open={processAutomaticDialogOpen}
						onOpenChange={setProcessAutomaticDialogOpen}
					>
						<Button onClick={() => setProcessAutomaticDialogOpen(true)}>
							<Zap className="mr-2 h-4 w-4" />
							Lancer les prélèvements automatiques
						</Button>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Lancer les prélèvements automatiques</DialogTitle>
								<DialogDescription>
									Cette action va traiter tous les paiements automatiques en
									attente pour les factures échues.
								</DialogDescription>
							</DialogHeader>
							<Alert className="my-4">
								<AlertTriangle className="h-4 w-4" />
								<AlertDescription>
									<strong>{stats.pendingInvoices}</strong> facture(s) seront
									traitées. Les prélèvements seront effectués via Stripe pour
									les parents avec paiement automatique activé.
								</AlertDescription>
							</Alert>
							<DialogFooter>
								<Button
									variant="outline"
									onClick={() => setProcessAutomaticDialogOpen(false)}
								>
									Annuler
								</Button>
								<Button
									onClick={handleProcessAutomatic}
									disabled={processAutomatic.isPending}
								>
									{processAutomatic.isPending
										? "Traitement..."
										: "Lancer les prélèvements"}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
			</div>

			{/* Statistiques */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Total paiements
						</CardTitle>
						<CreditCard className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{stats.totalPayments}</div>
						<p className="text-xs text-muted-foreground">
							{stats.successfulPayments} réussis
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Montant collecté
						</CardTitle>
						<Euro className="h-4 w-4 text-green-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{stats.totalAmount.toFixed(0)} €
						</div>
						<p className="text-xs text-muted-foreground">Total encaissé</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Échecs</CardTitle>
						<XCircle className="h-4 w-4 text-red-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-red-600">
							{stats.failedPayments}
						</div>
						<p className="text-xs text-muted-foreground">Paiements échoués</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Factures en attente
						</CardTitle>
						<AlertTriangle className="h-4 w-4 text-orange-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-orange-600">
							{stats.pendingInvoices}
						</div>
						<p className="text-xs text-muted-foreground">À régulariser</p>
					</CardContent>
				</Card>
			</div>

			{/* Historique des paiements */}
			<Card>
				<CardHeader>
					<CardTitle>Paiements</CardTitle>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={sortedPayments}
						emptyMessage="Aucun paiement enregistré"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="paiements">
								{table.getColumn("status") && (
									<DataTableFilterFaceted
										column={table.getColumn("status")!}
										title="Statut"
										options={statusOptions}
									/>
								)}
								{table.getColumn("paymentMethod") && (
									<DataTableFilterFaceted
										column={table.getColumn("paymentMethod")!}
										title="Méthode"
										options={paymentMethodOptions}
									/>
								)}
								{table.getColumn("paymentProvider") && (
									<DataTableFilterFaceted
										column={table.getColumn("paymentProvider")!}
										title="Provider"
										options={paymentProviderOptions}
									/>
								)}
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>

			{/* Dialog Paiement Manuel Multi-Factures */}
			<Dialog open={manualPaymentDialogOpen} onOpenChange={setManualPaymentDialogOpen}>
				<DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Créer un paiement manuel multi-factures</DialogTitle>
						<DialogDescription>
							Enregistrez un paiement reçu pour une ou plusieurs factures
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						{/* Mode de recherche */}
						<RadioGroup value={searchMode} onValueChange={(v: any) => {
							setSearchMode(v);
							setParentSearch("");
							setInvoiceSearch("");
							setSelectedParent(null);
							setSelectedInvoices([]);
							setPreview(null);
						}}>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="parent" id="mode-parent" />
								<Label htmlFor="mode-parent">Rechercher par parent</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="invoice" id="mode-invoice" />
								<Label htmlFor="mode-invoice">Rechercher par numéro de facture</Label>
							</div>
						</RadioGroup>

						{/* Recherche par parent */}
						{searchMode === "parent" && (
							<div>
								<Label>Parent</Label>
								<Input
									placeholder="Rechercher (nom, prénom, email, tél, nom enfant)..."
									value={parentSearch}
									onChange={(e) => setParentSearch(e.target.value)}
									className="mb-2"
								/>
								{parentSearch && (
									<div className="border rounded-lg max-h-48 overflow-y-auto">
										{parents
											.filter((p: any) => {
												const search = parentSearch.toLowerCase();
												const childrenMatch = p.students?.some((s: any) =>
													s.firstName?.toLowerCase().includes(search) ||
													s.lastName?.toLowerCase().includes(search)
												);
												return (
													p.firstName?.toLowerCase().includes(search) ||
													p.lastName?.toLowerCase().includes(search) ||
													p.email?.toLowerCase().includes(search) ||
													p.phone?.toLowerCase().includes(search) ||
													childrenMatch
												);
											})
											.slice(0, 5)
											.map((parent: any) => (
												<button
													key={parent.id}
													type="button"
													className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b last:border-b-0"
													onClick={() => {
														setSelectedParent(parent);
														setParentSearch(
															`${parent.firstName} ${parent.lastName} - ${parent.email}`
														);
														setSelectedInvoices([]);
														setCustomAmount("");
														setPreview(null);
													}}
												>
													<div className="text-sm font-medium">
														{parent.firstName} {parent.lastName}
													</div>
													<div className="text-xs text-gray-500">
														{parent.email} • {parent.phone}
													</div>
													{parent.students && parent.students.length > 0 && (
														<div className="text-xs text-gray-400 mt-1">
															Enfants: {parent.students.map((s: any) => `${s.firstName} ${s.lastName}`).join(", ")}
														</div>
													)}
												</button>
											))}
									</div>
								)}
								{selectedParent && !parentSearch.includes(selectedParent.email) && (
									<div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-sm">
										Parent sélectionné: <strong>{selectedParent.firstName} {selectedParent.lastName}</strong>
									</div>
								)}
							</div>
						)}

						{/* Recherche par facture */}
						{searchMode === "invoice" && (
							<div>
								<Label>Numéro de facture</Label>
								<Input
									placeholder="Rechercher une facture (ex: FAC-2024-001)..."
									value={invoiceSearch}
									onChange={(e) => setInvoiceSearch(e.target.value)}
									className="mb-2"
								/>
								{invoiceSearch && (
									<div className="border rounded-lg max-h-48 overflow-y-auto">
										{invoices
											.filter((inv: any) =>
												inv.invoiceNumber?.toLowerCase().includes(invoiceSearch.toLowerCase())
											)
											.slice(0, 5)
											.map((invoice: any) => (
												<button
													key={invoice.id}
													type="button"
													className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b last:border-b-0"
													onClick={() => {
														const parent = parents.find((p: any) => p.id === invoice.parent?.id);
														if (parent) {
															setSelectedParent(parent);
															setSelectedInvoices([invoice.id]);
															setInvoiceSearch(`${invoice.invoiceNumber} - ${invoice.parent?.firstName} ${invoice.parent?.lastName}`);
															setCustomAmount("");
															setPreview(null);
														}
													}}
												>
													<div className="text-sm font-medium">
														{invoice.invoiceNumber}
													</div>
													<div className="text-xs text-gray-500">
														{invoice.parent?.firstName} {invoice.parent?.lastName} •
														Montant: {invoice.totalAmount?.toFixed(2)} € •
														Restant: {(invoice.totalAmount - invoice.paidAmount)?.toFixed(2)} €
													</div>
													<div className="text-xs text-gray-400">
														Échéance: {invoice.dueDate ? format(new Date(invoice.dueDate), "dd/MM/yyyy", { locale: fr }) : "N/A"}
													</div>
												</button>
											))}
									</div>
								)}
								{selectedParent && searchMode === "invoice" && (
									<div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-sm">
										Parent: <strong>{selectedParent.firstName} {selectedParent.lastName}</strong><br />
										Facture sélectionnée: <strong>{selectedInvoices.length > 0 ? invoices.find((i: any) => i.id === selectedInvoices[0])?.invoiceNumber : ""}</strong>
									</div>
								)}
							</div>
						)}

						{selectedParent && (
							<>
								<Separator />

								{/* Choix: factures ou montant manuel */}
								<RadioGroup
									value={useCustomAmount ? "custom" : "invoices"}
									onValueChange={(v) => {
										setUseCustomAmount(v === "custom");
										if (v === "custom") {
											setSelectedInvoices([]);
										} else {
											setCustomAmount("");
										}
									}}
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="invoices" id="invoices" />
										<Label htmlFor="invoices">Sélectionner des factures</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="custom" id="custom" />
										<Label htmlFor="custom">Montant personnalisé</Label>
									</div>
								</RadioGroup>

								{!useCustomAmount && (
									<div className="space-y-2">
										<Label>Factures à payer ({parentOutstandingInvoices.length} en attente)</Label>
										<Input
											placeholder="Rechercher une facture par numéro..."
											value={invoiceSearch}
											onChange={(e) => setInvoiceSearch(e.target.value)}
											className="mb-2"
										/>
										<div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
											{parentOutstandingInvoices.length > 0 ? (
												parentOutstandingInvoices
													.filter((invoice: any) =>
														!invoiceSearch ||
														invoice.invoiceNumber?.toLowerCase().includes(invoiceSearch.toLowerCase())
													)
													.slice(0, 10)
													.map((invoice: any) => (
														<div key={invoice.id} className="flex items-center space-x-2">
															<Checkbox
																checked={selectedInvoices.includes(invoice.id)}
																onCheckedChange={(checked) => {
																	if (checked) {
																		setSelectedInvoices([...selectedInvoices, invoice.id]);
																	} else {
																		setSelectedInvoices(selectedInvoices.filter(id => id !== invoice.id));
																	}
																}}
															/>
															<div className="flex-1 flex justify-between items-center">
																<span className="text-sm">
																	{invoice.invoiceNumber} - Échéance: {format(new Date(invoice.dueDate), "dd/MM/yyyy", { locale: fr })}
																</span>
																<span className="text-sm font-medium">
																	{(invoice.totalAmount - invoice.paidAmount).toFixed(2)} €
																</span>
															</div>
														</div>
													))
											) : (
												<p className="text-sm text-gray-500">Aucune facture en attente</p>
											)}
										</div>
									</div>
								)}

								{useCustomAmount && (
									<div>
										<Label htmlFor="customAmount">Montant (€)</Label>
										<Input
											id="customAmount"
											type="number"
											step="0.01"
											value={customAmount}
											onChange={(e) => setCustomAmount(e.target.value)}
											placeholder="0.00"
										/>
										<p className="text-xs text-gray-500 mt-1">
											Le montant sera réparti automatiquement sur les factures par ordre d'ancienneté
										</p>
									</div>
								)}

								{/* Prévisualisation */}
								{preview && (
									<>
										<Separator />
										<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
											<div className="flex items-center gap-2 mb-3">
												<Eye className="h-4 w-4 text-blue-600" />
												<h4 className="font-medium text-blue-900">Prévisualisation de la répartition</h4>
											</div>
											<div className="space-y-2">
												{preview.allocations.map((alloc: any) => (
													<div key={alloc.invoiceId} className="flex justify-between text-sm">
														<span className="text-gray-700">{alloc.invoiceNumber}</span>
														<span className="font-medium">{alloc.allocatedAmount.toFixed(2)} €</span>
													</div>
												))}
												<Separator />
												<div className="flex justify-between font-bold">
													<span>Total</span>
													<span className="text-blue-600">{preview.totalAmount.toFixed(2)} €</span>
												</div>
											</div>
										</div>
									</>
								)}

								{preview && (
									<>
										<Separator />

										{/* Méthode de paiement */}
										<div>
											<Label>Méthode de paiement</Label>
											<Select value={paymentMethod} onValueChange={(v: any) => setPaymentMethod(v)}>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="MANUAL">Manuel</SelectItem>
													<SelectItem value="CARD">Carte bancaire</SelectItem>
													<SelectItem value="SEPA">Prélèvement SEPA</SelectItem>
													<SelectItem value="BANK_TRANSFER">Virement bancaire</SelectItem>
												</SelectContent>
											</Select>
										</div>

										{/* Provider */}
										<div>
											<Label>Fournisseur</Label>
											<Select value={paymentProvider} onValueChange={(v: any) => setPaymentProvider(v)}>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="HELLOASSO">HelloAsso</SelectItem>
													<SelectItem value="STRIPE">Stripe</SelectItem>
												</SelectContent>
											</Select>
										</div>

										{/* Commentaire */}
										<div>
											<Label htmlFor="comment">Commentaire (optionnel)</Label>
											<Textarea
												id="comment"
												value={comment}
												onChange={(e) => setComment(e.target.value)}
												placeholder="Ajouter un commentaire..."
												rows={3}
											/>
										</div>
									</>
								)}
							</>
						)}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={resetManualPaymentDialog}>
							Annuler
						</Button>
						<Button
							onClick={handleCreateManualPayment}
							disabled={!preview || createManualPayment.isPending}
						>
							{createManualPayment.isPending ? "Création..." : "Créer le paiement"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Dialog Effacement de Dette */}
			<Dialog open={debtDialogOpen} onOpenChange={setDebtDialogOpen}>
				<DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Effacer une dette</DialogTitle>
						<DialogDescription>
							Annuler une ou plusieurs factures sans encaissement réel
						</DialogDescription>
					</DialogHeader>

					<Alert>
						<Gift className="h-4 w-4" />
						<AlertDescription>
							L'effacement de dette créera un paiement avec le provider <strong>INTERNAL_DEBT_FORGIVENESS</strong>.
							Un commentaire justificatif est obligatoire.
						</AlertDescription>
					</Alert>

					<div className="space-y-4">
						{/* Sélection parent */}
						<div>
							<Label>Parent</Label>
							<Select
								value={debtParent?.id || ""}
								onValueChange={(value) => {
									const parent = parents.find((p: any) => p.id === value);
									setDebtParent(parent);
									setDebtInvoices([]);
									setDebtCustomAmount("");
									setDebtPreview(null);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="Sélectionner un parent" />
								</SelectTrigger>
								<SelectContent>
									{parents.map((parent: any) => (
										<SelectItem key={parent.id} value={parent.id}>
											{parent.firstName} {parent.lastName} - {parent.email}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{debtParent && (
							<>
								<Separator />

								{/* Choix: factures ou montant manuel */}
								<RadioGroup
									value={debtUseCustomAmount ? "custom" : "invoices"}
									onValueChange={(v) => {
										setDebtUseCustomAmount(v === "custom");
										if (v === "custom") {
											setDebtInvoices([]);
										} else {
											setDebtCustomAmount("");
										}
									}}
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="invoices" id="debt-invoices" />
										<Label htmlFor="debt-invoices">Sélectionner des factures</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="custom" id="debt-custom" />
										<Label htmlFor="debt-custom">Montant personnalisé</Label>
									</div>
								</RadioGroup>

								{!debtUseCustomAmount && (
									<div className="space-y-2">
										<Label>Factures à effacer ({debtParentOutstandingInvoices.length} en attente)</Label>
										<div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
											{debtParentOutstandingInvoices.length > 0 ? (
												debtParentOutstandingInvoices.map((invoice: any) => (
													<div key={invoice.id} className="flex items-center space-x-2">
														<Checkbox
															checked={debtInvoices.includes(invoice.id)}
															onCheckedChange={(checked) => {
																if (checked) {
																	setDebtInvoices([...debtInvoices, invoice.id]);
																} else {
																	setDebtInvoices(debtInvoices.filter(id => id !== invoice.id));
																}
															}}
														/>
														<div className="flex-1 flex justify-between items-center">
															<span className="text-sm">
																{invoice.invoiceNumber} - Échéance: {format(new Date(invoice.dueDate), "dd/MM/yyyy", { locale: fr })}
															</span>
															<span className="text-sm font-medium">
																{(invoice.totalAmount - invoice.paidAmount).toFixed(2)} €
															</span>
														</div>
													</div>
												))
											) : (
												<p className="text-sm text-gray-500">Aucune facture en attente</p>
											)}
										</div>
									</div>
								)}

								{debtUseCustomAmount && (
									<div>
										<Label htmlFor="debtCustomAmount">Montant (€)</Label>
										<Input
											id="debtCustomAmount"
											type="number"
											step="0.01"
											value={debtCustomAmount}
											onChange={(e) => setDebtCustomAmount(e.target.value)}
											placeholder="0.00"
										/>
										<p className="text-xs text-gray-500 mt-1">
											Le montant sera réparti automatiquement sur les factures par ordre d'ancienneté
										</p>
									</div>
								)}

								{/* Prévisualisation */}
								{debtPreview && (
									<>
										<Separator />
										<div className="bg-green-50 border border-green-200 rounded-lg p-4">
											<div className="flex items-center gap-2 mb-3">
												<Eye className="h-4 w-4 text-green-600" />
												<h4 className="font-medium text-green-900">Prévisualisation de l'effacement</h4>
											</div>
											<div className="space-y-2">
												{debtPreview.allocations.map((alloc: any) => (
													<div key={alloc.invoiceId} className="flex justify-between text-sm">
														<span className="text-gray-700">{alloc.invoiceNumber}</span>
														<span className="font-medium">{alloc.allocatedAmount.toFixed(2)} €</span>
													</div>
												))}
												<Separator />
												<div className="flex justify-between font-bold">
													<span>Total</span>
													<span className="text-green-600">{debtPreview.totalAmount.toFixed(2)} €</span>
												</div>
											</div>
										</div>
									</>
								)}

								{debtPreview && (
									<>
										<Separator />

										{/* Commentaire obligatoire */}
										<div>
											<Label htmlFor="debtComment">Justification (obligatoire)</Label>
											<Textarea
												id="debtComment"
												value={debtComment}
												onChange={(e) => setDebtComment(e.target.value)}
												placeholder="Expliquez la raison de l'effacement de dette..."
												rows={4}
												className="border-orange-300"
											/>
											<p className="text-xs text-orange-600 mt-1">
												Le commentaire est obligatoire pour justifier l'effacement
											</p>
										</div>
									</>
								)}
							</>
						)}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={resetDebtDialog}>
							Annuler
						</Button>
						<Button
							onClick={handleCreateDebtForgiveness}
							disabled={!debtPreview || !debtComment || createDebtForgiveness.isPending}
							className="bg-green-600 hover:bg-green-700"
						>
							{createDebtForgiveness.isPending ? "Effacement..." : "Effacer la dette"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
